import { resolveConditionLabel } from '@/components/Functional/utils/rewardCondition'
import {
  type RewardConditionGroupNode,
  type RewardConditionTreeNode,
  isTreeNodeAchieved,
} from '@/components/Functional/utils/rewardConditionTree'
import { Typography } from '@/components/UI/Typography'
import { type RenderTreeNodePayload, Tree, type TreeNodeData, getTreeExpandedState, useTree } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import type { TFunction } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classes from './RewardConditionTreeView.module.css'

/** 段下げ幅（px）。ガイド線の CSS は --level-offset から座標を計算するため自動で追従する */
const LEVEL_OFFSET = 16

/**
 * RewardConditionTreeNode[] を Mantine Tree の data と value→node の索引に変換する。
 *
 * TreeNodeData.nodeProps は Record<string, any> で any が伝播するため使わず、
 * value をキーにした Map を併せて返して renderNode 内で引く。
 */
function toMantineTreeData(nodes: RewardConditionTreeNode[]): {
  data: TreeNodeData[]
  nodeMap: Map<string, RewardConditionTreeNode>
  /** value → 同じ親を持つノード一覧（自分自身を含む）。最下層グループのアコーディオンで使う */
  siblingsMap: Map<string, RewardConditionTreeNode[]>
} {
  const nodeMap = new Map<string, RewardConditionTreeNode>()
  const siblingsMap = new Map<string, RewardConditionTreeNode[]>()

  const convert = (node: RewardConditionTreeNode, siblings: RewardConditionTreeNode[]): TreeNodeData => {
    nodeMap.set(node.key, node)
    siblingsMap.set(node.key, siblings)
    return {
      // label は renderNode を指定しているため使われないが、型上必須なので key を入れておく
      label: node.key,
      value: node.key,
      children: node.children.map((child) => convert(child, node.children)),
    }
  }

  return { data: nodes.map((node) => convert(node, nodes)), nodeMap, siblingsMap }
}

/**
 * 「一番下の階層のグループ」か。子がすべて末端条件のグループを指す。
 * このグループどうしだけを排他的に開く（アコーディオン）。
 * 上位のグループ（配下にさらにグループを持つもの）は排他にしない
 * ── 上位まで排他にすると、開いた枝以外の構造がまったく見えなくなるため。
 *
 * 呼び出し側で depth >= 2 も併せて判定すること。トップレベルのグループは
 * 子がすべて末端条件でもこの関数は true を返すが、排他の対象にはしない。
 */
function isDeepestGroup(node: RewardConditionTreeNode): boolean {
  return node.kind === 'group' && node.children.length > 0 && node.children.every((c) => c.kind === 'condition')
}

/**
 * グループ行のラベル。
 * displayName（管理画面で設定した名称）があればそれを優先し、
 * 無い場合のみ operator から既定文言を組み立てる。
 */
function resolveGroupLabel(t: TFunction, group: RewardConditionGroupNode): string {
  if (group.displayName) return group.displayName
  return group.operator === 'AND'
    ? t('reward_detail.condition_group_and', 'すべての条件を満たす')
    : t('reward_detail.condition_group_or', 'いずれか{{count}}件の条件を満たす', { count: group.threshold })
}

type Props = {
  /** buildRewardConditionTree() の戻り値 */
  nodes: RewardConditionTreeNode[]
  /**
   * 初期展開の深さ。
   * - 'none'（既定）: すべて折りたたんだ状態で始める
   * - 'top-level': トップレベルのグループだけ展開する
   *
   * 既定を 'none' にしているのは、開いた状態で始めると縦に長くなり
   * 説明文やボタンが押し下げられるため。中身は利用者が開いて確認する。
   */
  defaultExpanded?: 'top-level' | 'none'
  /**
   * 親子関係のガイド線（縦線 + 横のつなぎ線）を描くか。既定は描かない。
   * 段下げだけでも階層は読み取れるため、既定は線なしの軽い見た目にしている。
   */
  withLines?: boolean
  /**
   * 一番下の階層のグループを排他的に開くか（アコーディオン）。既定は有効。
   * 「9コースのうち2コース」のようなデータで、コースを開くたびに
   * 他のコースが閉じるため縦に伸びすぎない。
   */
  accordionDeepestLevel?: boolean
}

/**
 * 特典の獲得条件をツリーで表示する（#3581）。
 * 段数に上限は設けていない（バックエンドの MAX_DEPTH が増えてもそのまま動く）。
 *
 * 行のレイアウトは [開閉シェブロン] [条件テキスト]。
 * グループ配下の末端条件には中黒（・）を置き、兄弟のグループ行と列を揃える。
 * 階層は段下げで表す。ガイド線は withLines を付けたときだけ描く（既定は線なし）。
 * 一番下の階層のグループは排他的に開く（accordionDeepestLevel、既定は有効）。
 * 条件テキストは「スタンプ獲得状況 2/5」のように進捗を文言の直後に続ける
 * （ツリー化前の獲得条件テキストと同じ書式）。
 * シェブロンは子を持つグループ行にのみ出し、末端条件はその分の余白を取らない
 * （グループが無い特典でツリー化前と同じ位置に条件テキストを出すため）。
 * 字形はグループ／末端条件・達成／未達成で変えず、ツリー化前の獲得条件テキストと同じ
 * caption1（12px / 400 / 16px）+ text-gray-500 を踏襲する。
 * 達成状態は進捗の数値と、読み上げ用の視覚的非表示テキストで伝える。
 *
 * ⚠️ 呼び出し側は nodes が空でないことを確認してからマウントし、特典ごとに key を付けること。
 *    Mantine の useTree は initialExpandedState をマウント時にしか読まないため、
 *    空配列でマウントしたり特典切り替えで作り直さなかったりすると、
 *    defaultExpanded で開くはずのグループが折りたたまれたまま表示される。
 */
export function RewardConditionTreeView({
  nodes,
  defaultExpanded = 'none',
  withLines = false,
  accordionDeepestLevel = true,
}: Props) {
  const { t } = useTranslation()

  // Tree は data の参照が変わるたび initialize() を呼ぶため、必ず memo 化する
  const { data, nodeMap, siblingsMap } = useMemo(() => toMantineTreeData(nodes), [nodes])

  // 初期展開状態。'none'（既定）は空配列を渡してすべて折りたたむ。
  // 'top-level' ならトップレベルのグループだけ開き、2 段目以降は折りたたむ。
  const initialExpandedState = useMemo(
    () =>
      getTreeExpandedState(
        data,
        defaultExpanded === 'none'
          ? []
          : data.filter((node) => (node.children?.length ?? 0) > 0).map((node) => node.value),
      ),
    [data, defaultExpanded],
  )
  const tree = useTree({ initialExpandedState })

  const renderNode = ({ node, expanded, hasChildren, elementProps }: RenderTreeNodePayload) => {
    const target = nodeMap.get(node.value)
    if (!target) return null

    const achieved = isTreeNodeAchieved(target)
    // 「{条件名} {達成数}/{必要数}」。書式はツリー化前の獲得条件テキストと揃える。
    // 数値だけウェイトを上げるため、条件名と数値を別要素で描き分ける。
    // displayName も既定文言も無い条件（LOGIN_DAYS 等）は label が null になり、
    // ツリー化前と同じく数値だけを表示する。
    const label = target.kind === 'group' ? resolveGroupLabel(t, target) : resolveConditionLabel(t, target.condition)
    // OR グループでは分子が分母を超えることがあるが、丸めずそのまま表示する
    // （Math.min で丸めると「実際に何件達成したか」の情報が落ちるため）。
    const { current, threshold } = target.kind === 'group' ? target : target.condition
    // 「いま開いている枝」をまとまりとして太字にする。
    //
    // - 2 階層目以降の開いているグループそのもの
    // - それを開いたことで現れた 3 階層目以降の行
    //
    // Mantine は親が閉じているとき子を描画しないため、3 階層目以降が
    // renderNode に来た時点で親（2 階層目のグループ）は開いている。
    // よって深さだけで判定できる。
    //
    // トップレベル（depth === 1）は対象外。トップレベルはツリー全体の入り口であり、
    // ここを太字にしても「いまどの枝を見ているか」の手がかりにならないため。
    // トップレベル直下の末端条件（depth === 2 で子を持たない）も、
    // 何かを開いて現れたわけではないので対象外。
    const isOpenedGroup = hasChildren && expanded && target.depth >= 2
    const isInsideOpenedGroup = target.depth >= 3
    const isInOpenedBranch = isOpenedGroup || isInsideOpenedGroup

    return (
      // elementProps.className は Mantine の label スタイル。潰さず連結する。
      // items-start により、条件名が折り返しても 1 行目にシェブロンが固定される
      // （CSS の --tree-row-center と一致させ、ガイド線の横線とズレないようにするため）。
      // caption1 は行高 16px でシェブロンも 16px のため、上マージンの補正は不要。
      <div
        {...elementProps}
        onClick={(event) => {
          // Mantine 側のハンドラ（expandedState のトグル・フォーカス）を先に走らせる
          elementProps.onClick(event)

          // 一番下の階層のグループを「開いた」ときだけ、同じ親の兄弟グループを閉じる。
          // expanded はクリック前の状態なので、!expanded === 今回の操作で開いた、を意味する。
          // Mantine の toggleExpanded も tree.collapse も関数型の setState なので、
          // 同じイベント内で順に積まれ、トグル → 兄弟を閉じる の順で確定する。
          // depth >= 2 に限る。トップレベルのグループを排他にすると、
          // 並列に置かれた複数のトップレベルグループのうち 1 つを開くたびに
          // 他が閉じてしまい、ツリー全体の構造が見えなくなるため。
          if (!accordionDeepestLevel || expanded || target.depth < 2 || !isDeepestGroup(target)) return
          for (const sibling of siblingsMap.get(target.key) ?? []) {
            if (sibling.key !== target.key && isDeepestGroup(sibling)) tree.collapse(sibling.key)
          }
        }}
        className={`${elementProps.className} flex items-start gap-[8px] py-[6px] ${
          // Mantine は全ノードに cursor: pointer を付けるが、末端条件は押しても何も起きない
          hasChildren ? '' : 'cursor-default'
        }`}
      >
        {/* 先頭の 16px 枠。子を持つ行には開閉シェブロン、
         * グループ配下の末端条件（depth > 1）には中黒（・）を置く。
         * アイコンコンポーネントではなく文字をそのまま出す。
         * シェブロンと同じ 16px 幅に収めて中央寄せするので、兄弟のグループ行と列が揃う。
         * 行の字形（caption1 = 12px / 行高 16px）に合わせておかないと
         * 周囲のフォントサイズを継いでしまい、シェブロンと縦位置がずれる。
         *
         * トップレベルの末端条件（depth === 1）には何も置かない。
         * ここにスペーサーを置くと、グループが無い特典では全行が末端条件になり、
         * 条件テキスト全体が 24px（16 + gap8）右に寄ってしまうため、
         * ツリー化前と同じ位置に出すことを優先する。 */}
        {hasChildren ? (
          <IconChevronDown
            size={16}
            stroke={1.5}
            className={`shrink-0 text-gray-500 transition-transform ${expanded ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
        ) : (
          target.depth > 1 && (
            <span className="w-[16px] shrink-0 text-center text-[12px] leading-[16px] text-gray-500" aria-hidden="true">
              ・
            </span>
          )
        )}

        {/* 条件テキスト。進捗の数値は文言の直後に続ける（ツリー化前と同じ書式）。
         * 字形はグループ／末端条件で変えず、ツリー化前の獲得条件テキストと同じ
         * caption1（12px / 400 / 16px）+ text-gray-500 を踏襲する。
         *
         * 開いている枝（2 階層目のグループと、それを開いて現れた 3 階層目以降）を
         * 太字にして、いまどこを見ているかを示す。色は変えない。
         *
         * ⚠️ 太字は Typography の style prop ではなく「子要素の class」で当てること。
         * Typography の variant を style で上書きするのは規約違反。
         * また Typography は variantStyle をインラインの style で当てている
         * （fontWeight: 400）ため、Typography 自身に font-bold を付けても
         * インライン style に負けて効かない。子要素なら自分の class が優先される。 */}
        <Typography variant="caption1" component="span" className="min-w-0 flex-1 text-gray-500">
          <span className={isInOpenedBranch ? 'font-bold' : undefined}>
            {label !== null && `${label} `}
            {/* 進捗の数値だけウェイトを上げる（caption1 の 400 → 700）。
             * ⚠️ font-medium（500）にしないこと。本プロジェクトは Web フォントを読み込んでおらず、
             * 'Noto Sans JP' は Hiragino 等のシステムフォントにフォールバックする。
             * それらは Regular(400) と Bold(700) しか字面を持たないため、
             * CSS のフォントマッチングで 500 は 400 にスナップされ、見た目がまったく変わらない。 */}
            <span className="font-bold">{`${current}/${threshold}`}</span>
            {achieved && <span className="sr-only">{t('reward_detail.condition_achieved', '達成済み')}</span>}
          </span>
        </Typography>
      </div>
    )
  }

  return (
    <Tree
      data={data}
      tree={tree}
      renderNode={renderNode}
      classNames={{
        root: withLines ? `${classes.root} ${classes.withLines}` : classes.root,
        node: classes.node,
        subtree: classes.subtree,
      }}
      levelOffset={LEVEL_OFFSET}
      allowRangeSelection={false}
      aria-label={t('reward_detail.condition_tree_aria', '獲得条件')}
    />
  )
}
